import { postRequest } from "@/api/request";
import JwtService from "@/common/jwt.service";
import i18nService from "@/common/i18n.service";

// Used after normal login and after mandatory enrollment succeeds.
export async function hydrateAdminSession(token) {
  const response = await postRequest({ action: "getAdminInfo", token });
  if (!response || response.returncode !== 1)
    throw new Error("Admin session unavailable");
  const info = response.returndata;
  JwtService.saveLoginUser(info.adminname);
  JwtService.saveTfaState(info.twofa_enabled);
  JwtService.saveTfaAllowedState(info.twofa_allowed);
  JwtService.saveSecurityWarningState(info.security_warning);
  JwtService.saveSecurityWarningLevel(info.security_warning_level);
  JwtService.saveUserRole(info.adminaccess);
  JwtService.saveTimeout(info.ui_timeout);
  JwtService.saveComplexPass(info.complexpass);
  JwtService.saveDomainsWithoutMx(info.domainswithoutmx);
  i18nService.setActiveLanguage(info.language);
  const permissions = await postRequest({ action: "getPermissions", token });
  if (!permissions || permissions.returncode !== 1)
    throw new Error("Admin permissions unavailable");
  JwtService.savePermission(permissions.returndata);
}

// Impersonation policy is issued by the API; the browser never infers a bypass
// from the target's MFA flag or from a parameter in the login link.
export function readAdminSessionPolicy(result) {
  const policy = result && result.returncode === 1 && result.returndata;
  if (!policy || typeof policy.mfa_enrollment_required !== "boolean") {
    throw new Error("Admin session unavailable");
  }
  if (policy.is_impersonating === true &&
      (policy.mfa_enrollment_required !== false || policy.enrollment_allowed !== false)) {
    throw new Error("Invalid impersonation policy");
  }
  return policy;
}

export async function acceptIncomingAdminToken(token) {
  const policy = readAdminSessionPolicy(
    await postRequest({ action: "getMfaEnrollmentStatus", token })
  );
  JwtService.saveToken(token);
  if (policy.mfa_enrollment_required) return "/mfa-enrollment";
  await hydrateAdminSession(token);
  return "/home";
}

export function createMfaRouteGuard({
  request,
  getToken,
  saveTfaState,
  hydrateIfNeeded = async () => {},
}) {
  return async (to, from, next) => {
    if (["/login", "/forgotPass"].includes(to.path)) return next();
    const token = getToken();
    if (!token) return next("/login");
    try {
      const result = await request({ action: "getMfaEnrollmentStatus", token });
      const policy = readAdminSessionPolicy(result);
      saveTfaState(policy["2fa_enabled"]);
      if (policy.mfa_enrollment_required) {
        return next(
          to.path === "/mfa-enrollment" ? undefined : "/mfa-enrollment"
        );
      }
      await hydrateIfNeeded(token);
      return next(to.path === "/mfa-enrollment" ? "/home" : undefined);
    } catch (_) {
      return next("/login");
    }
  };
}
